const DEFAULT_STARTING_SCORE = 501;
const DEFAULT_BEST_OF_LEGS = 9;

function nowIso() {
  return new Date().toISOString();
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function initialPlayerState(startingScore = DEFAULT_STARTING_SCORE) {
  return {
    remaining: Number(startingScore || DEFAULT_STARTING_SCORE),
    legsWon: 0,
    visits: [],
    dartsThrown: 0,
    pointsScored: 0,
  };
}

export function createFixtureLiveSession({
  fixtureId = '',
  league = 'PDL',
  season = '',
  home = {},
  away = {},
  startingScore = DEFAULT_STARTING_SCORE,
  bestOfLegs = DEFAULT_BEST_OF_LEGS,
  deviceId = '',
} = {}) {
  const createdAt = nowIso();
  return {
    fixtureId,
    league,
    season,
    deviceId,
    status: 'draft',
    revision: 0,
    createdAt,
    updatedAt: createdAt,
    controller: {
      deviceId,
      online: true,
      pendingEvents: 0,
      lastClientSeq: 0,
      lastAckSeq: 0,
    },
    config: {
      startingScore: Number(startingScore || DEFAULT_STARTING_SCORE),
      bestOfLegs: Number(bestOfLegs || DEFAULT_BEST_OF_LEGS),
    },
    participants: {
      home: {
        teamId: home.teamId || '',
        teamName: home.teamName || 'Home',
        playerIds: Array.isArray(home.playerIds) ? home.playerIds : [],
        ...initialPlayerState(startingScore),
      },
      away: {
        teamId: away.teamId || '',
        teamName: away.teamName || 'Away',
        playerIds: Array.isArray(away.playerIds) ? away.playerIds : [],
        ...initialPlayerState(startingScore),
      },
    },
    live: {
      currentLegNumber: 1,
      currentTurn: 'home',
      legStarter: 'home',
      winner: '',
      recentEvents: [],
    },
    offline: {
      queue: [],
      lastSyncedAt: null,
      dirty: false,
    },
  };
}

export function buildLocalEvent(type, payload = {}, session = {}) {
  const nextSeq = Number(session?.controller?.lastClientSeq || 0) + 1;
  return {
    eventId: `${session.deviceId || 'device'}:${nextSeq}`,
    deviceId: session.deviceId || '',
    clientSeq: nextSeq,
    type,
    payload: clone(payload),
    createdAt: nowIso(),
  };
}

function otherSide(side) {
  return side === 'home' ? 'away' : 'home';
}

function pushRecentEvent(state, event) {
  state.live.recentEvents = [...(state.live.recentEvents || []), {
    type: event.type,
    createdAt: event.createdAt || nowIso(),
    payload: clone(event.payload || {}),
  }].slice(-20);
}

function applyVisitRecorded(state, event) {
  const side = String(event?.payload?.side || '');
  const score = Number(event?.payload?.score || 0);
  const darts = Math.max(1, Number(event?.payload?.darts || 3));
  const checkout = !!event?.payload?.checkout;
  if (!['home', 'away'].includes(side)) return state;

  const player = state.participants[side];
  const nextRemaining = Math.max(0, Number(player.remaining || 0) - score);
  player.remaining = nextRemaining;
  player.dartsThrown += darts;
  player.pointsScored += score;
  player.visits.push({
    score,
    darts,
    checkout,
    createdAt: event.createdAt || nowIso(),
  });

  if (checkout || nextRemaining === 0) {
    player.legsWon += 1;
    state.live.winner = side;
    state.status = player.legsWon > Math.floor(Number(state.config.bestOfLegs || DEFAULT_BEST_OF_LEGS) / 2)
      ? 'completed'
      : 'live';
    if (state.status !== 'completed') {
      const nextStarter = otherSide(state.live.legStarter);
      state.live.currentLegNumber += 1;
      state.live.legStarter = nextStarter;
      state.live.currentTurn = nextStarter;
      state.participants.home.remaining = Number(state.config.startingScore || DEFAULT_STARTING_SCORE);
      state.participants.away.remaining = Number(state.config.startingScore || DEFAULT_STARTING_SCORE);
      state.participants.home.visits = [];
      state.participants.away.visits = [];
    }
  } else {
    state.live.currentTurn = otherSide(side);
  }

  return state;
}

function applyUndo(state) {
  const home = state.participants.home;
  const away = state.participants.away;
  const hl = Array.isArray(home.visits) ? home.visits.length : 0;
  const al = Array.isArray(away.visits) ? away.visits.length : 0;
  if (!hl && !al) return state;
  const homeLast = hl ? home.visits[hl - 1] : null;
  const awayLast = al ? away.visits[al - 1] : null;
  const ht = homeLast?.createdAt ? Date.parse(homeLast.createdAt) : 0;
  const at = awayLast?.createdAt ? Date.parse(awayLast.createdAt) : 0;
  const side =
    awayLast && (!homeLast || at > ht || (at === ht && al >= hl))
      ? 'away'
      : homeLast
        ? 'home'
        : 'away';
  const player = state.participants[side];
  const lastVisit = player.visits.pop();
  if (!lastVisit) return state;
  player.remaining += Number(lastVisit.score || 0);
  player.dartsThrown = Math.max(0, Number(player.dartsThrown || 0) - Number(lastVisit.darts || 0));
  player.pointsScored = Math.max(0, Number(player.pointsScored || 0) - Number(lastVisit.score || 0));
  state.live.currentTurn = side;
  return state;
}

export function applyFixtureLiveEvent(currentState, event) {
  const state = clone(currentState);
  state.revision = Number(state.revision || 0) + 1;
  state.updatedAt = event?.createdAt || nowIso();
  state.controller.lastClientSeq = Math.max(
    Number(state.controller?.lastClientSeq || 0),
    Number(event?.clientSeq || 0),
  );

  switch (String(event?.type || '')) {
    case 'session.started':
      state.status = 'live';
      state.live.currentTurn = String(event?.payload?.startingSide || state.live.legStarter || 'home');
      state.live.legStarter = state.live.currentTurn;
      break;
    case 'session.paused':
      state.status = 'paused';
      break;
    case 'session.resumed':
      state.status = 'live';
      break;
    case 'visit.recorded':
      applyVisitRecorded(state, event);
      break;
    case 'visit.undone':
      applyUndo(state);
      break;
    case 'session.completed':
      state.status = 'completed';
      state.live.winner = String(event?.payload?.winner || state.live.winner || '');
      break;
    default:
      break;
  }

  pushRecentEvent(state, event);
  return state;
}

export function enqueueFixtureLiveEvent(session, event) {
  const next = applyFixtureLiveEvent(session, event);
  next.offline.queue.push(clone(event));
  next.offline.dirty = true;
  next.controller.pendingEvents = next.offline.queue.length;
  return next;
}

export function acknowledgeFixtureLiveEvents(session, ackClientSeq = 0, serverRevision = null) {
  const next = clone(session);
  next.offline.queue = (next.offline.queue || []).filter((event) => Number(event.clientSeq || 0) > Number(ackClientSeq || 0));
  next.offline.dirty = next.offline.queue.length > 0;
  next.offline.lastSyncedAt = nowIso();
  next.controller.pendingEvents = next.offline.queue.length;
  next.controller.lastAckSeq = Math.max(Number(next.controller.lastAckSeq || 0), Number(ackClientSeq || 0));
  if (serverRevision !== null && serverRevision !== undefined) {
    next.revision = Math.max(Number(next.revision || 0), Number(serverRevision || 0));
  }
  return next;
}

export function mergeFixtureLiveSnapshot(localSession, serverSnapshot) {
  const local = clone(localSession);
  const remote = clone(serverSnapshot);
  const pending = clone(local.offline?.queue || []);
  let merged = remote;
  for (const event of pending) {
    merged = applyFixtureLiveEvent(merged, event);
  }
  merged.offline = {
    ...(merged.offline || {}),
    queue: pending,
    dirty: pending.length > 0,
    lastSyncedAt: local.offline?.lastSyncedAt || null,
  };
  merged.controller = {
    ...(merged.controller || {}),
    pendingEvents: pending.length,
    lastClientSeq: Math.max(
      Number(local.controller?.lastClientSeq || 0),
      Number(merged.controller?.lastClientSeq || 0),
    ),
    lastAckSeq: Math.max(
      Number(local.controller?.lastAckSeq || 0),
      Number(merged.controller?.lastAckSeq || 0),
    ),
  };
  return merged;
}

export function fixtureLiveStorageKey(fixtureId, deviceId = 'device') {
  return `pdl.fixture-live.${fixtureId}.${deviceId}`;
}

export function loadFixtureLiveSession(storage, fixtureId, deviceId = 'device') {
  if (!storage || !fixtureId) return null;
  try {
    const raw = storage.getItem(fixtureLiveStorageKey(fixtureId, deviceId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveFixtureLiveSession(storage, session) {
  if (!storage || !session?.fixtureId) return;
  storage.setItem(
    fixtureLiveStorageKey(session.fixtureId, session.deviceId || 'device'),
    JSON.stringify(session),
  );
}
